import React, { useState } from "react";
import { LandingPageRow } from "../LandingPageSection";
import styled from "@emotion/styled";
import { CustomThemeType } from "../../../../context/Providers";
import CallToActionButton from "../../../../components/CallToActionButton/CallToActionButton";
import Button from "../../../../components/Button/Button";
import { useWindowSize } from "react-use";
import { addDoc, collection } from "firebase/firestore";
import { db } from "../../../../firebaseConfig";

const Col1 = styled.div<{ theme?: CustomThemeType }>`
    align-self: flex-start;
    width: 35%;
    height: 50vh;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    margin: 6rem 60px;
    z-index: 10;
    h1 {
        color: ${p => p.theme.foreground};
        padding: 0;
        margin: 0;
        font-weight: 500;
        font-size: 4rem;
    }
    h4 {
        color: color-mix(in hsl, ${p => p.theme.foreground}, ${p => p.theme.background} 60%);
        margin-bottom: 4.5rem;
        margin-top: 3rem;
        font-size: 2rem;
    }
    @media (max-width: 1425px) {
        h1 {
            font-size: 3rem;
        }
        h4 {
        }
    }
    @media (max-width: 1200px) {
        width: 60%;
        align-items: center;
        margin: 2rem 2rem;
        height: auto;
        h1 {
        }
        h4 {
        }
    }
    @media (max-width: 850px) {
        width: 70%;
    }
    @media (max-width: 600px) {
        width: 80%;
        margin: 2rem 1rem;
        h1 {
        }
        h4 {
        }
    }
    @media (max-width: 525px) {
        width: 100%;
    }
`;

const Col2 = styled.div<any>`
    align-self: flex-start;
    width: 30%;
    height: 50vh;
    position: relative;
    margin: 6rem 60px;
    @media (max-width: 1200px) {
        display: none;
    }
    & h5 {
        text-align: center;
        font-size: 1.2rem;
        color: ${p => p.theme.foreground};
    }
    & h6 {
        text-align: center;
        font-size: 1rem;
        opacity: .5;
        padding-top: 0;
        margin-top: 0;
    }
`;

const ContactPhotoWrapper = styled.div`
    width: 100%;
    aspect-ratio: 1 / 1;
    border-radius: 50%;
    overflow: hidden;
    align-items: center;
    justify-content: center;
    position: relative;
    & img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        min-width: 100%;
        min-height: 100%;
        width: 100%;
        height: 100%;
        object-fit: cover;          /* ensures it fills and crops */
        /* override any global constraints if needed: */
        max-width: none;
        max-height: none;
    }
    @media (max-width: 1200px) {
        height: 15rem;
        aspect-ratio: 1 / 1;
        width: 15rem;
        align-self: center;
        margin-bottom: 1rem;
    }
`;

const FormWrapper = styled.form`
    display: flex;
    flex-direction: column;
    width: 100%;
    @media (max-width: 1200px) {
        & h1 {
            align-self: center;
        }
    }
`;

export const FormRow = styled.div<{ theme?: CustomThemeType, labelWidth: string; }>`
    margin-top: 1rem;
    display: flex;
    width: 100%;
    & label {
        margin: 0 .5rem;
        width: ${p => p.labelWidth};
        display: flex;
        box-sizing: border-box;
        flex-direction: column;
        gap: .1rem;
        color: ${p => p.theme.foreground};
        font-size: 1.2rem;
    }
    & input {
        color: ${p => p.theme.foreground};
        padding: .5rem;
        background-color: ${p => `color-mix(in hsl, ${p.theme.background}, ${p.theme.foreground} 10%)`};
        border: none;
        border-radius: .25rem;
        outline: none;
    }
    & textarea {
        color: ${p => p.theme.foreground};
        padding: .5rem;
        height: 10rem;
        resize: none;
        background-color: ${p => `color-mix(in hsl, ${p.theme.background}, ${p.theme.foreground} 10%)`};
        border: none;
        border-radius: .25rem;
        outline: none;
    }
`;

const SubmitWrapper = styled.div`
    margin-top: 1rem;
    margin-left: .5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
`;

const SuccessMessage = styled.span`
    color: #5abe5a;
`;

const ErrorMessage = styled.span`
    color: #ff6b6b;
`;

const ContactDetails = styled.div<{ theme?: CustomThemeType }>`
    margin: 1.5rem 0 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: 1.1rem;
    color: ${p => p.theme.foreground};
    a {
        color: inherit;
        text-decoration: none;
        font-weight: 600;
    }
    span {
        display: flex;
        flex-wrap: wrap;
        gap: 0.4rem;
    }
`;


interface AboutContentProps {

}

const AboutContent: React.FC<AboutContentProps> = () => {
    const { width } = useWindowSize();

    const [email, setEmail] = useState<string>('');
    const [fname, setFname] = useState<string>('');
    const [lname, setLname] = useState<string>('');
    const [message, setMessage] = useState<string>('');
    const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleSubmit = async () => {
        if (isSubmitting) {
            return;
        }

        const trimmedEmail = email.trim();
        const trimmedMessage = message.trim();
        const trimmedFname = fname.trim();
        const trimmedLname = lname.trim();

        if (!trimmedEmail || !trimmedMessage) {
            setErrorMessage("Please provide an email address and a short message.");
            return;
        }

        const payload: Record<string, any> = {
            email: trimmedEmail,
            message: trimmedMessage,
            source: "landingPage"
        };

        if (trimmedFname) {
            payload.fname = trimmedFname;
        }

        if (trimmedLname) {
            payload.lname = trimmedLname;
        }

        setIsSubmitting(true);
        setErrorMessage(null);

        try {
            await addDoc(collection(db, "getInTouch"), payload);
            setHasSubmitted(true);
            setEmail('');
            setFname('');
            setLname('');
            setMessage('');
        } catch (error) {
            console.error("[AboutContent] Failed to submit contact form", error);
            setErrorMessage("We couldn't submit your request. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    }

    const isSubmitDisabled = isSubmitting || !email.trim() || !message.trim();

    return (
        <LandingPageRow height={width > 1200 ? "100vh" : "auto"}>
            {width > 1200 && (
                <Col2>
                    <ContactPhotoWrapper>
                        <img src="./assets/contact-photo.png" />
                    </ContactPhotoWrapper>
                    <h5>Cristopher Blunt</h5>
                </Col2>
            )}
            <Col1>
                <FormWrapper>
                {width <= 1200 && (
                    <ContactPhotoWrapper>
                        <div style={{height: '5rem'}} />
                        <img src="./assets/contact-photo.png" />
                    </ContactPhotoWrapper>
                )}
                <h1>Get in touch</h1>
                <ContactDetails>
                    <span>
                        Email:
                        <a href="mailto:cristopher.blunt@cabinetscanner.com">
                            cristopher.blunt@cabinetscanner.com
                        </a>
                    </span>
                    <span>
                        Phone:
                        <a href="tel:8323221452">
                            (832) 322-1452
                        </a>
                    </span>
                </ContactDetails>
                    {width > 600 ? (
                        <FormRow labelWidth="calc(50% - 1rem)">
                            <label>
                                First Name:
                                <input value={fname} onChange={(e) => setFname(e.target.value)}></input>
                            </label>
                            <label>
                                Last Name:
                                <input value={lname} onChange={(e) => setLname(e.target.value)}></input>
                            </label>
                        </FormRow>) : (
                        <>
                            <FormRow labelWidth="100%">
                                <label>
                                    First Name:
                                    <input value={fname} onChange={(e) => setFname(e.target.value)}></input>
                                </label>
                            </FormRow>
                            <FormRow labelWidth="100%">
                                <label>
                                    Last Name:
                                    <input value={lname} onChange={(e) => setLname(e.target.value)}></input>
                                </label>

                            </FormRow>
                        </>
                    )}
                    <FormRow labelWidth="100%">
                        <label>
                            Email:
                            <input value={email} onChange={(e) => setEmail(e.target.value)}></input>
                        </label>
                    </FormRow>
                    <FormRow labelWidth="100%">
                        <label>
                            Message:
                            <textarea value={message} onChange={(e) => setMessage(e.target.value)}></textarea>
                        </label>
                    </FormRow>
                    <FormRow labelWidth="100%">
                        <SubmitWrapper>
                            {hasSubmitted ? (
                                <SuccessMessage>Thank you! We'll follow up shortly!</SuccessMessage>
                            ) : (
                                <>
                                    <Button size="sm" onClick={() => handleSubmit()} disabled={isSubmitDisabled}>
                                        {isSubmitting ? "Submitting..." : "Submit"}
                                    </Button>
                                    {errorMessage && (
                                        <ErrorMessage>{errorMessage}</ErrorMessage>
                                    )}
                                </>
                            )}
                        </SubmitWrapper>
                    </FormRow>
                </FormWrapper>
            </Col1>
        </LandingPageRow>
    );
}

export default AboutContent;
